import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { notificationApi, showError } from "../lib";
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  Loader,
  Clock,
  BookOpen,
  MessageCircle,
  Video,
  Award,
  Trophy,
  FileText,
  Users,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Calendar,
  Gift,
  Shield,
  CreditCard,
} from "lucide-react";

interface NotificationItem {
  _id: string;
  title: string;
  content: string;
  type: string;
  link?: string;
  isRead: boolean;
  createdAt: string;
}

// Notification type configurations with different colors and backgrounds
const notificationConfigs: Record<
  string,
  {
    icon: any;
    bgGradient: string;
    iconBg: string;
    borderColor: string;
    badgeColor: string;
    textColor: string;
  }
> = {
  session: {
    icon: Video,
    bgGradient: "from-blue-50 to-indigo-50",
    iconBg: "bg-blue-500",
    borderColor: "border-blue-200",
    badgeColor: "bg-blue-500",
    textColor: "text-blue-700",
  },
  course: {
    icon: BookOpen,
    bgGradient: "from-purple-50 to-pink-50",
    iconBg: "bg-purple-500",
    borderColor: "border-purple-200",
    badgeColor: "bg-purple-500",
    textColor: "text-purple-700",
  },
  message: {
    icon: MessageCircle,
    bgGradient: "from-green-50 to-emerald-50",
    iconBg: "bg-green-500",
    borderColor: "border-green-200",
    badgeColor: "bg-green-500",
    textColor: "text-green-700",
  },
  review: {
    icon: Award,
    bgGradient: "from-amber-50 to-yellow-50",
    iconBg: "bg-amber-500",
    borderColor: "border-amber-200",
    badgeColor: "bg-amber-500",
    textColor: "text-amber-700",
  },
  enrollment: {
    icon: FileText,
    bgGradient: "from-indigo-50 to-blue-50",
    iconBg: "bg-indigo-500",
    borderColor: "border-indigo-200",
    badgeColor: "bg-indigo-500",
    textColor: "text-indigo-700",
  },
  achievement: {
    icon: Trophy,
    bgGradient: "from-orange-50 to-amber-50",
    iconBg: "bg-orange-500",
    borderColor: "border-orange-200",
    badgeColor: "bg-orange-500",
    textColor: "text-orange-700",
  },
  payment: {
    icon: CreditCard,
    bgGradient: "from-emerald-50 to-teal-50",
    iconBg: "bg-emerald-500",
    borderColor: "border-emerald-200",
    badgeColor: "bg-emerald-500",
    textColor: "text-emerald-700",
  },
  mentorship: {
    icon: Users,
    bgGradient: "from-cyan-50 to-sky-50",
    iconBg: "bg-cyan-500",
    borderColor: "border-cyan-200",
    badgeColor: "bg-cyan-500",
    textColor: "text-cyan-700",
  },
  system: {
    icon: Bell,
    bgGradient: "from-gray-50 to-slate-50",
    iconBg: "bg-gray-500",
    borderColor: "border-gray-200",
    badgeColor: "bg-gray-500",
    textColor: "text-gray-700",
  },
  calendar: {
    icon: Calendar,
    bgGradient: "from-rose-50 to-pink-50",
    iconBg: "bg-rose-500",
    borderColor: "border-rose-200",
    badgeColor: "bg-rose-500",
    textColor: "text-rose-700",
  },
  promotion: {
    icon: Gift,
    bgGradient: "from-fuchsia-50 to-purple-50",
    iconBg: "bg-fuchsia-500",
    borderColor: "border-fuchsia-200",
    badgeColor: "bg-fuchsia-500",
    textColor: "text-fuchsia-700",
  },
  security: {
    icon: Shield,
    bgGradient: "from-slate-50 to-gray-50",
    iconBg: "bg-slate-500",
    borderColor: "border-slate-200",
    badgeColor: "bg-slate-500",
    textColor: "text-slate-700",
  },
  default: {
    icon: Bell,
    bgGradient: "from-gray-50 to-gray-100",
    iconBg: "bg-gray-500",
    borderColor: "border-gray-200",
    badgeColor: "bg-gray-500",
    textColor: "text-gray-700",
  },
};

const formatTimeAgo = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now.getTime() - date.getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m`;
  if (hours < 24) return `${hours}h`;
  if (days < 7) return `${days}d`;
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [showClearModal, setShowClearModal] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;
  const observerRef = useRef<IntersectionObserver | null>(null);
  const observedIds = useRef<Set<string>>(new Set());

  const markNotificationAsRead = useCallback(
    async (notificationId: string) => {
      if (observedIds.current.has(notificationId)) return;
      observedIds.current.add(notificationId);

      try {
        await notificationApi.markAsRead(notificationId);
        setNotifications((prev) =>
          prev.map((n) =>
            n._id === notificationId ? { ...n, isRead: true } : n,
          ),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (err) {
        console.error("Failed to auto-mark as read:", err);
      }
    },
    [],
  );

   useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.getAttribute("data-notification-id");
          if (id && entry.isIntersecting) {
            const notification = notifications.find((n) => n._id === id);
            if (notification && !notification.isRead) {
              markNotificationAsRead(id);
            }
            observerRef.current?.unobserve(entry.target);
          }
        });
      },
      { threshold: 0, rootMargin: "0px 0px -15% 0px" },
    );

    return () => {
      observerRef.current?.disconnect();
    };
  }, [notifications, markNotificationAsRead]);

  const setObserverRef = useCallback(
    (node: HTMLDivElement | null, id: string) => {
      if (node && observerRef.current && !observedIds.current.has(id)) {
        observerRef.current.observe(node);
      }
    },
    [],
  );

  const filteredNotifications =
    filter === "unread"
      ? notifications.filter((notification) => !notification.isRead)
      : notifications;

  useEffect(() => {
    fetchNotifications(1);
    fetchUnreadCount();
  }, [filter]);

  const fetchNotifications = async (targetPage: number) => {
    try {
      setLoading(true);
      const response = await notificationApi.getNotifications({
        page: targetPage,
        limit,
        unreadOnly: filter === "unread",
      });
      const data = response?.data || [];
      setNotifications(data);
      setPage(targetPage);
      setPages(response?.pagination?.pages || 1);
      setTotal(response?.pagination?.total || 0);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await notificationApi.getUnreadCount();
      setUnreadCount(response?.unreadCount || 0);
    } catch (err) {
      console.error("Failed to fetch unread count:", err);
      setUnreadCount(0);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notificationId ? { ...n, isRead: true } : n,
        ),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark as read:", err);
      showError("Failed to mark as read");
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all as read:", err);
      showError("Failed to mark all as read");
    }
  };

  const handleDelete = async (notificationId: string) => {
    try {
      await notificationApi.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n._id !== notificationId));
    } catch (err) {
      console.error("Failed to delete notification:", err);
      showError("Failed to delete notification");
    }
  };

  const handleClearAll = async () => {
    setClearing(true);
    try {
      await notificationApi.clearAll();
      setNotifications([]);
      setUnreadCount(0);
      setShowClearModal(false);
    } catch (err) {
      console.error("Failed to clear all:", err);
      showError("Failed to clear all notifications");
    } finally {
      setClearing(false);
    }
  };

  const getNotificationLink = (notification: NotificationItem) => {
    if (notification.link) return notification.link;
    switch (notification.type) {
      case "session":
        return "/dashboard/mentor/sessions";
      case "course":
        return "/courses";
      case "message":
        return "/dashboard/student/messages";
      case "mentorship":
        return "/mentorship";
      case "payment":
        return "/dashboard/student/payments";
      case "achievement":
        return "/dashboard/student/certificates";
      default:
        return "/dashboard/student";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader className="w-10 h-10 mx-auto mb-3 text-orange-500 animate-spin" />
          <p className="text-sm text-gray-500">Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="max-w-4xl px-3 mx-auto sm:px-4 md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-4 sm:mb-5">
          <div className="flex items-center gap-2 sm:gap-3 mb-1">
            <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-lg shadow-sm bg-gradient-to-r from-amber-600 to-orange-500">
              <Bell className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 md:text-2xl">
              Notifications
            </h1>
          </div>
          <p className="ml-10 sm:ml-12 text-xs sm:text-sm text-gray-500 md:text-base">
            {unreadCount > 0
              ? `${unreadCount} unread notification${unreadCount > 1 ? "s" : ""}`
              : "All caught up!"}
          </p>
        </div>

        {/* Filter Bar */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex gap-1.5">
            <button
              onClick={() => setFilter("all")}
              className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all ${
                filter === "all"
                  ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              All
              <span
                className={`ml-1 px-1 py-0.5 text-[10px] rounded-full ${
                  filter === "all"
                    ? "bg-white/20 text-white"
                    : "bg-gray-100 text-gray-600"
                }`}
              >
                {total}
              </span>
            </button>
            <button
              onClick={() => setFilter("unread")}
              className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all ${
                filter === "unread"
                  ? "bg-gradient-to-r from-orange-600 to-orange-500 text-white shadow-sm"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              Unread
              {unreadCount > 0 && (
                <span className="ml-1 px-1 py-0.5 text-[10px] rounded-full bg-red-500 text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>
          </div>

          <div className="flex gap-1.5 sm:gap-2">
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-all"
              >
                <CheckCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Mark all read</span>
              </button>
            )}
            {notifications.length > 0 && (
              <button
                onClick={() => setShowClearModal(true)}
                className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-all"
              >
                <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Clear all</span>
              </button>
            )}
          </div>
        </div>

        {/* Notifications List - Facebook Style */}
        {filteredNotifications.length === 0 ? (
          <div className="py-12 text-center bg-white border border-gray-100 shadow-sm rounded-xl">
            <div className="flex items-center justify-center w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-br from-gray-100 to-gray-200">
              <Bell className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-gray-900">
              No notifications
            </h3>
            <p className="max-w-xs mx-auto text-sm text-gray-500">
              {filter === "unread"
                ? "You've read all your notifications"
                : "You're all caught up! New notifications will appear here."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => {
              const config =
                notificationConfigs[notification.type] ||
                notificationConfigs.default;
              const Icon = config.icon;
              const link = getNotificationLink(notification);
              const timeAgo = formatTimeAgo(notification.createdAt);

              return (
                <div
                  key={notification._id}
                  ref={(node) => setObserverRef(node, notification._id)}
                  data-notification-id={notification._id}
                  className={`group relative overflow-hidden transition-all duration-200 rounded-xl ${
                    !notification.isRead
                      ? "bg-blue-50 border-l-4 border-l-blue-500 shadow-md hover:shadow-lg"
                      : "bg-white border border-gray-100 hover:shadow-md"
                  }`}
                >
                  <div className="p-3 sm:p-4">
                    <div className="flex gap-2 sm:gap-3 md:gap-4">
                      {/* Icon */}
                      <div
                        className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 ${config.iconBg} shadow-sm`}
                      >
                        <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-start justify-between gap-1">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
                              <h4
                                className={`text-xs sm:text-sm font-semibold ${config.textColor} truncate`}
                              >
                                {notification.title}
                              </h4>
                              {!notification.isRead && (
                                <span className="px-1.5 py-0.5 text-[9px] sm:text-[10px] font-bold text-white bg-red-500 rounded-full">
                                  NEW
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-xs sm:text-sm leading-relaxed text-gray-600 line-clamp-2 sm:line-clamp-none">
                              {notification.content}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 text-[10px] sm:text-xs text-gray-400 whitespace-nowrap shrink-0 mt-0.5">
                            <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                            {timeAgo}
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex flex-wrap items-center gap-2 pt-2 mt-3 border-t border-gray-200/50">
                           <Link
                             to={link}
                             onClick={() => {
                               if (!notification.isRead) {
                                 handleMarkAsRead(notification._id);
                               }
                             }}
                             className={`px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm font-medium rounded-lg transition-all ${config.textColor} bg-opacity-10 hover:bg-opacity-20 bg-gray-100 hover:bg-gray-200`}
                           >
                             View
                           </Link>

                            {!notification.isRead && (
                              <button
                                onClick={() => handleMarkAsRead(notification._id)}
                                className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-blue-600 rounded-lg hover:bg-blue-50 transition-all"
                              >
                                <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">Mark read</span>
                              </button>
                            )}

                            <button
                              onClick={() => handleDelete(notification._id)}
                              className="flex items-center gap-1 px-2.5 sm:px-3 py-1.5 text-xs sm:text-sm text-gray-600 hover:text-red-600 rounded-lg hover:bg-red-50 ml-auto transition-all"
                            >
                              <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              <span className="hidden sm:inline">Delete</span>
                            </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between gap-2 pt-4 mt-4 border-t border-gray-200">
            <button
              onClick={() => fetchNotifications(page - 1)}
              disabled={page <= 1}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-all"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Prev</span>
            </button>

            <span className="text-sm text-gray-500">
              Page {page} of {pages}
            </span>

            <button
              onClick={() => fetchNotifications(page + 1)}
              disabled={page >= pages}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-all"
            >
              <span>Next</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Footer Stats */}
        {notifications.length > 0 && (
          <div className="pt-3 mt-3 text-center border-t border-gray-200">
            <p className="text-xs text-gray-400">
              {total} total · {unreadCount} unread
            </p>
          </div>
        )}
      </div>

      {/* Clear All Confirmation Modal */}
      {showClearModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-sm overflow-hidden bg-white shadow-2xl rounded-2xl animate-fade-in">
            <div className="flex items-center gap-3 p-4 border-b border-red-100 bg-gradient-to-r from-red-50 to-orange-50">
              <div className="flex items-center justify-center w-10 h-10 bg-red-100 rounded-full">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-gray-900">
                  Clear All Notifications
                </h3>
                <p className="text-xs text-gray-500">
                  This action cannot be undone
                </p>
              </div>
            </div>

            <div className="p-4">
              <p className="text-sm text-gray-600">
                Are you sure you want to delete all {total} notifications? You
                will lose your entire notification history.
              </p>
            </div>

            <div className="flex gap-3 p-4 pt-0">
              <button
                onClick={() => setShowClearModal(false)}
                className="flex-1 px-3 py-2 text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleClearAll}
                disabled={clearing}
                className="flex-1 px-3 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5"
              >
                {clearing ? (
                  <Loader className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                {clearing ? "Clearing..." : "Clear All"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: scale(0.95);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.6;
          }
        }
        .animate-pulse {
          animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </div>
  );
};

export default NotificationsPage;